import React from 'react';
import axios from 'axios';
import { useQuery } from 'react-query';

// Só tipos: o vite não resolve import de runtime de @webip/shared.
import type { AlertaIntervaloDTO, AlertaSeveridade } from '@webip/shared/src/dto/alerta';

/**
 * Faixa do tempo dos alertas: uma raia por severidade, pintada nos trechos em
 * que havia ao menos um alerta daquela severidade ativo. Raia própria com
 * rótulo escrito (e não só a cor) porque vermelho e âmbar ficam próximos para
 * quem tem deficiência de cor. Hover/teclado: linha vertical + o que estava
 * ativo naquele instante.
 */

const RAIAS: { severidade: AlertaSeveridade; rotulo: string; cor: string }[] = [
    { severidade: 'critico', rotulo: 'Crítico', cor: 'var(--status-offline)' },
    { severidade: 'aviso', rotulo: 'Aviso', cor: 'var(--status-warn)' },
    { severidade: 'info', rotulo: 'Informativo', cor: 'var(--status-info)' },
];

const ROTULO_W = 88;
const RAIA_H = 16;
const RAIA_GAP = 6;
const EIXO_H = 20;
const ALTURA = RAIAS.length * (RAIA_H + RAIA_GAP) + EIXO_H;

type Trecho = { ini: number; fim: number };

/** União dos intervalos de uma severidade, recortada na janela. */
function uniao(intervalos: AlertaIntervaloDTO[], ini: number, fim: number, agora: number): Trecho[] {
    const tr = intervalos
        .map((i) => ({ ini: Math.max(Date.parse(i.inicio), ini), fim: Math.min(i.fim ? Date.parse(i.fim) : agora, fim) }))
        .filter((t) => t.fim > t.ini)
        .sort((a, b) => a.ini - b.ini);
    const out: Trecho[] = [];
    for (const t of tr) {
        const ult = out[out.length - 1];
        if (ult && t.ini <= ult.fim) ult.fim = Math.max(ult.fim, t.fim);
        else out.push({ ...t });
    }
    return out;
}

function marcas(ini: number, fim: number, largura: number): { t: number; rotulo: string }[] {
    const span = fim - ini;
    const h = 3_600_000;
    // Acima de 2 dias a marca é por dia: "16:00" sem o dia não diz qual.
    const passos = (span > 2 * 24 * h ? [] : [h, 3 * h, 6 * h, 12 * h]).concat([24 * h, 2 * 24 * h, 5 * 24 * h, 7 * 24 * h]);
    const maxMarcas = Math.max(2, Math.floor(largura / 70));
    const passo = passos.find((p) => span / p <= maxMarcas) ?? passos[passos.length - 1];
    const porDia = passo >= 24 * h;
    // Alinha no fuso local (meia-noite / hora cheia).
    const primeiro = new Date(ini);
    if (porDia) primeiro.setHours(24, 0, 0, 0);
    else primeiro.setMinutes(60, 0, 0);
    const out: { t: number; rotulo: string }[] = [];
    for (let t = primeiro.getTime(); t < fim; t += passo) {
        const d = new Date(t);
        out.push({
            t,
            rotulo: porDia
                ? d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' })
                : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
        });
    }
    return out;
}

const FaixaDoTempo = ({ dias, applianceId }: { dias: number; applianceId: string | null }) => {
    const boxRef = React.useRef<HTMLDivElement>(null);
    const [largura, setLargura] = React.useState(600);
    const [cursor, setCursor] = React.useState<number | null>(null);

    React.useEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([e]) => setLargura(Math.max(240, e.contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const query = useQuery({
        queryKey: ['alertas-linha-do-tempo', dias, applianceId],
        queryFn: async () => {
            const res = await axios.get<{ result: AlertaIntervaloDTO[] }>('/api/alerta/linha-do-tempo', {
                params: { dias, appliance_id: applianceId ?? undefined },
                withCredentials: true,
            });
            return res.data.result;
        },
        refetchInterval: 60_000,
        keepPreviousData: true,
    });

    const agora = Date.now();
    const ini = agora - dias * 86_400_000;
    const plotW = largura - ROTULO_W;
    const x = (t: number) => ROTULO_W + ((t - ini) / (agora - ini)) * plotW;
    const intervalos = query.data ?? [];

    const porSeveridade = React.useMemo(() => {
        const m = new Map<AlertaSeveridade, Trecho[]>();
        for (const r of RAIAS) m.set(r.severidade, uniao(intervalos.filter((i) => i.severidade === r.severidade), ini, agora, agora));
        return m;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intervalos, dias]);

    const ativosEm = (t: number) => intervalos.filter((i) => Date.parse(i.inicio) <= t && (i.fim ? Date.parse(i.fim) : agora) >= t);

    const moverCursor = (clientX: number) => {
        const el = boxRef.current;
        if (!el) return;
        const px = clientX - el.getBoundingClientRect().left;
        if (px < ROTULO_W) return setCursor(null);
        setCursor(ini + ((px - ROTULO_W) / plotW) * (agora - ini));
    };

    const onKey = (e: React.KeyboardEvent) => {
        const passo = (agora - ini) / 100;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const base = cursor ?? agora;
            setCursor(Math.min(agora, Math.max(ini, base + (e.key === 'ArrowLeft' ? -passo : passo))));
        } else if (e.key === 'Escape') {
            setCursor(null);
        }
    };

    const ativos = cursor != null ? ativosEm(cursor) : [];
    const tooltipX = cursor != null ? x(cursor) : 0;
    const semNada = !query.isLoading && intervalos.length === 0;

    return (
        <div style={{ position: 'relative', opacity: query.isFetching && query.data ? 0.6 : 1, transition: 'opacity 150ms' }}>
            <div
                ref={boxRef}
                tabIndex={0}
                role="img"
                aria-label={`Faixa do tempo dos alertas nos últimos ${dias} dia(s). Use as setas para percorrer.`}
                onPointerMove={(e) => moverCursor(e.clientX)}
                onPointerLeave={() => setCursor(null)}
                onKeyDown={onKey}
                onBlur={() => setCursor(null)}
                style={{ outlineOffset: 2, cursor: 'crosshair' }}
            >
                <svg width={largura} height={ALTURA} style={{ display: 'block' }}>
                    {RAIAS.map((r, i) => {
                        const y = i * (RAIA_H + RAIA_GAP);
                        return (
                            <g key={r.severidade}>
                                <text x={0} y={y + RAIA_H - 4} fontSize={12} fill="var(--tc-color-gray-700)">{r.rotulo}</text>
                                <rect x={ROTULO_W} y={y} width={plotW} height={RAIA_H} rx={3} fill="var(--tc-color-gray-100)" />
                            </g>
                        );
                    })}
                    {/* Grade atrás dos trechos. */}
                    {marcas(ini, agora, plotW).map((m) => (
                        <g key={m.t}>
                            <line x1={x(m.t)} x2={x(m.t)} y1={0} y2={ALTURA - EIXO_H} stroke="var(--tc-color-gray-200)" strokeWidth={1} />
                            <text x={x(m.t)} y={ALTURA - 5} fontSize={11} textAnchor="middle" fill="var(--tc-color-gray-600)">{m.rotulo}</text>
                        </g>
                    ))}
                    {RAIAS.map((r, i) => {
                        const y = i * (RAIA_H + RAIA_GAP);
                        return (
                            <g key={`${r.severidade}-trechos`}>
                                {(porSeveridade.get(r.severidade) ?? []).map((t) => (
                                    <rect
                                        key={t.ini}
                                        x={x(t.ini)}
                                        y={y}
                                        // Trecho curto ainda aparece (≥ 2 px).
                                        width={Math.max(2, x(t.fim) - x(t.ini))}
                                        height={RAIA_H}
                                        rx={3}
                                        fill={r.cor}
                                    />
                                ))}
                            </g>
                        );
                    })}
                    {cursor != null && (
                        <line x1={tooltipX} x2={tooltipX} y1={0} y2={ALTURA - EIXO_H} stroke="var(--tc-color-gray-800)" strokeWidth={1} />
                    )}
                </svg>
            </div>

            {semNada && (
                <span style={{ position: 'absolute', left: ROTULO_W + 8, top: RAIA_H + 2, fontSize: 12, color: 'var(--tc-color-gray-600)' }}>
                    Nenhum alerta no período.
                </span>
            )}

            {cursor != null && (
                <div
                    role="status"
                    style={{
                        position: 'absolute',
                        top: ALTURA + 4,
                        left: Math.min(Math.max(0, tooltipX - 140), largura - 280),
                        width: 280,
                        zIndex: 5,
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: 'var(--surface-elevated)',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                        border: '1px solid var(--tc-color-gray-200)',
                        fontSize: 12,
                        pointerEvents: 'none',
                    }}
                >
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>{new Date(cursor).toLocaleString()}</div>
                    {ativos.length === 0 ? (
                        <span style={{ color: 'var(--tc-color-gray-600)' }}>Nenhum alerta ativo.</span>
                    ) : (
                        RAIAS.map((r) => {
                            const lista = ativos.filter((a) => a.severidade === r.severidade);
                            if (!lista.length) return null;
                            return (
                                <div key={r.severidade} style={{ marginTop: 4 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ width: 12, height: 2, background: r.cor, display: 'inline-block' }} />
                                        <b>{lista.length}</b>
                                        <span style={{ color: 'var(--tc-color-gray-700)' }}>{r.rotulo.toLowerCase()}</span>
                                    </div>
                                    {lista.slice(0, 4).map((a) => (
                                        <div key={a.id} style={{ paddingLeft: 18, color: 'var(--tc-color-gray-700)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {a.titulo}
                                        </div>
                                    ))}
                                    {lista.length > 4 && (
                                        <div style={{ paddingLeft: 18, color: 'var(--tc-color-gray-600)' }}>e mais {lista.length - 4}</div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

export default FaixaDoTempo;
